// Source: D:/AI/site-queensbutler/docs/structure/04_詳細設計_ページ内容スキーマ.md §2, §4
import routesData from '@/data/routes.json'
import type { FixedRouteData, RouteManifest } from '@/data/types'
import type { PageContent } from './contentTypes'
import { getLang, stripEn } from './lang'
import { globalNavEn } from '@/data/nav.en'

const modules = import.meta.glob<PageContent>('../content/pages/P-*.json', {
  eager: true,
  import: 'default',
}) as Record<string, PageContent>

// 英語ページ(docs/_prompts/20260917_en_site_spec.md §2-2)。同 id・同構造で url が `/en/...`
const modulesEn = import.meta.glob<PageContent>('../content/pages-en/P-*.json', {
  eager: true,
  import: 'default',
}) as Record<string, PageContent>

export const allPageContent = Object.values(modules)
export const pageContentByUrl = new Map(allPageContent.map((page) => [page.url, page]))
export const pageContentById = new Map(allPageContent.map((page) => [page.id, page]))
export const allPageContentEn = Object.values(modulesEn)
export const pageContentEnByUrl = new Map(allPageContentEn.map((page) => [page.url, page]))
export const pageContentEnById = new Map(allPageContentEn.map((page) => [page.id, page]))

/** EN が無いページを JA で代替してよいか(開発中の暫定確認のみ。本番は fail-closed) */
export function allowMissingEn(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_QB_ALLOW_MISSING === '1'
}

export const fixedRoutes = (routesData as RouteManifest).routes
export const routeByUrl = new Map(fixedRoutes.map((route) => [route.url, route]))

export function normalizePageUrl(pathname: string): string {
  if (pathname === '/') return pathname
  return pathname.endsWith('/') ? pathname : `${pathname}/`
}

/** pathname の言語に応じた本文。`/en/` は EN マップ(url キー)。EN 未作成は VITE_QB_ALLOW_MISSING=1 のときだけ JA を返す */
export function getPageContent(pathname: string): PageContent | undefined {
  const url = normalizePageUrl(pathname)
  if (getLang(url) === 'en') {
    return pageContentEnByUrl.get(url) ?? (allowMissingEn() ? pageContentByUrl.get(stripEn(url)) : undefined)
  }
  return pageContentByUrl.get(url)
}

/** ルートは JA URL で管理する(routes.json)。EN URL は `/en` を外して引く */
export function getRoute(pathname: string): FixedRouteData | undefined {
  return routeByUrl.get(stripEn(normalizePageUrl(pathname)))
}

export function getRouteAncestors(route: FixedRouteData): FixedRouteData[] {
  const ancestors: FixedRouteData[] = []
  const seen = new Set<string>()
  let parentUrl = route.parentUrl

  while (parentUrl && parentUrl !== '/' && !seen.has(parentUrl)) {
    seen.add(parentUrl)
    const parent = routeByUrl.get(parentUrl)
    if (!parent) break
    ancestors.unshift(parent)
    parentUrl = parent.parentUrl
  }
  return ancestors
}

export function createInterimContent(route: FixedRouteData): PageContent {
  return {
    id: route.id,
    url: route.url,
    service: route.group === 'salon' || route.group === 'studio' || route.group === 'apprentice' || route.group === 'shop'
      ? route.group
      : null,
    layout: route.kind === 'L' ? 'legal' : route.kind === 'F' ? 'form' : route.kind === 'H' ? 'hub' : 'detail',
    meta: {
      title: `${route.name}｜Queen's Butler（クイーンズバトラー）`,
      description: route.summary || `${route.name}について、Queen's Butlerがご案内いたします。`,
    },
    h1: route.name,
    billboard: { pattern: route.kind === 'L' ? 'A-3b' : 'A-3a', eyebrow: route.id },
    definition: '本文準備中でございます。',
    sections: [],
    structuredData: { breadcrumb: true },
  }
}

/** EN ページのナビ表示名(ドロップダウン子項目・パンくず): EN コンテンツの meta.navLabel ?? h1(仕様 §2-7) */
export function pageLabel(route: FixedRouteData, lang: 'ja' | 'en'): string {
  if (lang === 'ja') return route.name
  const en = pageContentEnById.get(route.id)
  // サービストップ等は h1 が長い(例: "Queen's Butler The Studio — Studio & Rental Space")ので、navLabel が無ければグローバル nav の短いラベルを使う
  const navShort = globalNavEn.find((item) => stripEn(item.href) === route.url)?.label
  return en?.meta.navLabel ?? navShort ?? en?.h1 ?? route.name
}
